import type { ComplianceSubjectMetadata, TaxRate, TaxStatusEnum, ActionableContext } from 'venn-api';
import { globalSearch } from 'venn-api';
import { type AnalysisSubject, hasFeesExcluded, logMessageToSentry } from 'venn-utils';
import type { StudioRequestSubject, SubjectWithOptionalFeeAndFilters } from 'venn-state';
import { getSubjectId } from 'venn-state';
import { compact, uniq } from 'lodash';

interface BuildComplianceMetadataParams {
  subjects: SubjectWithOptionalFeeAndFilters[];
  analysisSubjects: AnalysisSubject[];
  requestSubjects: StudioRequestSubject[];
  taxRatesPerSubject: Record<string, TaxRate> | undefined;
  workspaceDefaultTaxRate: TaxRate | undefined;
  getContext: (contextId: string) => ActionableContext | undefined;
  viewOwnerContextId: string | undefined;
}

const hasTaxStatus = (taxStatuses: TaxStatusEnum[] | undefined, status: TaxStatusEnum): boolean =>
  taxStatuses?.includes(status) ?? false;

const fetchPortfolioLibraryLookup = async (
  portfolioIds: number[],
): Promise<Map<number, { household: string | null; client: string | null }>> => {
  const byPortfolioId = new Map<number, { household: string | null; client: string | null }>();

  if (portfolioIds.length === 0) {
    return byPortfolioId;
  }

  const response = await globalSearch({
    portfolioIds,
    pageSize: portfolioIds.length,
  });

  for (const entity of response.content.results) {
    if (entity.portfolioId) {
      byPortfolioId.set(entity.portfolioId, {
        household: entity.householdName ?? null,
        client: entity.clientName ?? null,
      });
    }
  }

  const missing = portfolioIds.filter((id) => !byPortfolioId.has(id));
  if (missing.length > 0) {
    logMessageToSentry(`Compliance metadata library lookup missing portfolio metadata for ids: ${missing.join(', ')}`);
  }

  return byPortfolioId;
};

export const buildComplianceMetadata = async ({
  subjects,
  analysisSubjects,
  requestSubjects,
  taxRatesPerSubject,
  workspaceDefaultTaxRate,
  getContext,
  viewOwnerContextId,
}: BuildComplianceMetadataParams): Promise<ComplianceSubjectMetadata[]> => {
  const portfolioIds = uniq(compact(subjects.map((s) => s.portfolioId)));
  const byPortfolioId = await fetchPortfolioLibraryLookup(portfolioIds);

  const metadata: ComplianceSubjectMetadata[] = [];

  for (let i = 0; i < subjects.length; i++) {
    const subject = subjects[i]!;
    const analysisSubject = analysisSubjects[i];
    if (!analysisSubject) {
      continue;
    }

    if (analysisSubject.private) {
      continue;
    }

    const isPortfolio = subject.portfolioId !== undefined;
    const isInvestment = subject.fundId !== undefined;
    if (!isPortfolio && !isInvestment) {
      continue;
    }

    const subjectKey = getSubjectId(subject);
    const subjectName = analysisSubject.name;
    const subjectType = isPortfolio ? 'PORTFOLIO' : 'INVESTMENT';

    const portfolio = analysisSubject.portfolio;
    let ownerContextId: string | null;
    let ownership: string | null;
    if (isPortfolio) {
      ownerContextId = portfolio?.ownerContextId ?? viewOwnerContextId ?? null;
      ownership = (ownerContextId ? getContext(ownerContextId) : undefined)?.context.name ?? null;
    } else {
      const fundOwnerIds = compact(analysisSubject.fund?.ownerContextIds);
      const resolved = compact(
        fundOwnerIds.map((id) => {
          const name = getContext(id)?.context.name;
          return name ? { id, name } : null;
        }),
      );
      ownership = resolved.length === 0 ? null : resolved.map((r) => r.name).join(', ');
      ownerContextId = resolved.length === 1 ? resolved[0]!.id : null;
    }

    const libraryInfo =
      isPortfolio && subject.portfolioId !== undefined ? byPortfolioId.get(subject.portfolioId) : undefined;
    const household = libraryInfo?.household ?? null;
    const client = libraryInfo?.client ?? null;

    const feesMapping = subject.feesMapping;
    const advisoryFee = feesMapping ? (feesMapping[subjectKey] ?? Object.values(feesMapping)[0] ?? null) : null;
    const requestSubject = requestSubjects[i];
    const hasInvestmentsExcludedFromFees = isPortfolio && requestSubject ? hasFeesExcluded(requestSubject) : null;

    const taxStatuses = portfolio?.taxStatuses;
    const hasTaxableInvestments = hasTaxStatus(taxStatuses, 'TAXABLE');
    const hasTaxDeferredInvestments = hasTaxStatus(taxStatuses, 'TAX_DEFERRED');
    const hasTaxExemptInvestments = hasTaxStatus(taxStatuses, 'TAX_EXEMPT');

    const taxRate = taxRatesPerSubject?.[subjectKey] ?? workspaceDefaultTaxRate;

    metadata.push({
      subjectName,
      subjectId: subjectKey,
      subjectType,
      household,
      ownership,
      ownerContextId,
      client,
      advisoryFee,
      hasInvestmentsExcludedFromFees,
      hasTaxableInvestments,
      hasTaxDeferredInvestments,
      hasTaxExemptInvestments,
      ltCapitalGainsRate: taxRate?.fedLtRate ?? null,
      stCapitalGainsRate: taxRate?.fedStRate ?? null,
      niitRate: taxRate?.niitRate ?? null,
      stateTaxRate: taxRate?.stateRate ?? null,
      capitalLossDeductionCap: taxRate?.lossCap ?? null,
    });
  }

  return metadata;
};
